import { pricingAPI } from '../../payments/services/PaymentPricingApi';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  Calendar, ChevronLeft, ChevronRight, Clock, MapPin, Navigation,
  Check, HelpCircle, X, Plus, RefreshCw, List, Edit2, Save, Trash2, FileText, ExternalLink, Users, AlertCircle, DollarSign
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';
import { getWebOrigin } from '../../utils/helpers';
import { useEvents } from '../hooks/useEvents';
import { getCurrentUser, getToken } from '../../profile/services';
import {
  getDateFromTimestamp,
  getTimeFromTimestamp
} from '../../utils/dateTimeUtils';
import AddEventModal from './AddEventModal';
import EventInviteesModal from './EventInviteesModal';
import EventResponseGroups from './EventResponseGroups';
import ImportFromPdfModal from './ImportFromPdfModal';
import GooglePlacesAutocomplete from '../../components/common/GooglePlacesAutocomplete';
import { ApplyToSeriesConfirm, RecurringScopeModal } from './SeriesScopeModals';
import { PricingPanel, formatMoney } from '../../payments/components/PaymentPriceModal';

import { getConfig } from '../../config';

// Capacity and courts render in three places — the calendar card, the list
// card and the details modal — so they live in one component. `compact` is the
// card styling; the modal wants the same rows a size larger.
// "$15/person" for a session with one price, "from $10/person" when there is a
// choice. The unit follows the charge model rather than being fixed: a monthly
// pass reading "/person" would be a lie about what is being bought.
const PRICE_UNIT = {
  per_session: '/person',
  one_off:     '/person',
  monthly:     '/month',
  full_term:   '/term',
};

/**
 * `unit` because a court is not sold per person.
 *
 * A session charges each player who turns up; a court is hired whole, by the
 * hour. Printing "/person" against a court's rate would be an invitation to
 * divide it by four.
 */
/** Where an inherited rate came from, said plainly rather than as a level name. */
const INHERITED_FROM = {
  event_series:  '(from the series)',
  program:       '(from the programme)',
  court:         '(the court\'s rate)',
  court_default: '(the court\'s default)',
};

const summarisePrice = (prices, unit = null) => {
  const live = (prices || []).filter(p => p && p.amount != null);
  if (!live.length) return null;
  const cheapest = live.reduce((a, b) => (Number(b.amount) < Number(a.amount) ? b : a));
  return {
    text: `${formatMoney(cheapest.amount, cheapest.currency)}${unit || PRICE_UNIT[cheapest.pricingModel] || '/person'}`,
    from: live.length > 1,
    label: cheapest.label || null,
  };
};

const SessionMeta = ({ event, courts, theme, compact = false, unit = null }) => {
  const hasCapacity = event?.capacity != null;
  const courtIds = event?.courtIds || [];
  const price = summarisePrice(event?.prices, unit);
  if (!hasCapacity && courtIds.length === 0 && !price) return null;

  const icon = compact ? 'w-4 h-4' : 'w-5 h-5';
  const text = compact ? `text-xs ${theme.textMuted}` : `text-sm ${theme.text}`;

  return (
    <>
      {(hasCapacity || price) && (
        <div className="flex items-center gap-2 flex-wrap">
          {hasCapacity && (
            <>
              <Users className={`${icon} ${theme.textMuted} flex-shrink-0`} />
              <span className={text}>
                {event.capacity} {event.capacity === 1 ? 'spot' : 'spots'}
              </span>
            </>
          )}
          {price && (
            <>
              {hasCapacity && <span className={theme.textMuted}>·</span>}
              {!hasCapacity && <DollarSign className={`${icon} ${theme.textMuted} flex-shrink-0`} />}
              <span className={`${text} font-medium`}>
                {price.from ? `from ${price.text}` : price.text}
              </span>
            </>
          )}
        </div>
      )}
      {courtIds.length > 0 && (
        <div className="flex items-start gap-x-2">
          <MapPin className={`${icon} ${theme.textMuted} flex-shrink-0 mt-0.5`} />
          <div className="flex flex-wrap gap-1.5 min-w-0">
            {courtIds.map(id => {
              // The court list only loads for sessions; until it arrives (or if
              // a court was since deleted) show a neutral chip, not a gap.
              const court = courts.find(c => c.id === id);
              return (
                <span key={id}
                  className={`px-2 py-0.5 rounded-md text-xs font-medium border ${theme.border} ${compact ? theme.textMuted : theme.text}`}>
                  {court?.name || 'Court'}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

const CalendarAndEvent = ({
  ownerType, ownerId, isMember, isAdmin = false, academyId = null, onEventResponseUpdate,
  // A squad's members, for who a new event invites and who can be added later.
  squadMembers = null, isClassSquad = false,
  // Whether the viewer may say if they are coming. Off when the calendar is
  // mounted as an admin tool — from settings the host is managing the sessions,
  // not attending them, and their own RSVP would be noise on every row.
  // Counts stay either way; those are what the host is there to read.
  allowResponses = true,
}) => {
  // Capacity, courts and pricing belong to an offering's sessions. A squad
  // event has no academy behind it, so they are neither shown nor sent.
  // `isSession` was doing three jobs at once: show capacity and courts, and
  // show pricing. A court needs the third and not the first two — its hours are
  // priced by the hour, and it has no spots and is not held on some other
  // court. Split so a new owner type can take what applies to it.
  const isSession = ownerType === 'program';
  const isCourt   = ownerType === 'court';
  // Anything sold has prices attached where they vary: a night, a series, or an
  // open window on a court.
  const isPriced  = isSession || isCourt;

  /**
   * What a court event IS, as a chip beside its name.
   *
   * A court's diary lists three unlike things whose names all came from
   * elsewhere — a session's title, a member's hire, an old booking's note — so
   * without this the rows read as a pile of programme events and nothing says
   * which hours are for sale and which are already gone.
   */
  /**
   * Is this row somebody's booking, rather than the court merely being open?
   *
   * KindChip already knew the difference — the month cell did not, which is how
   * a hire ended up indistinguishable from an opening window and then hidden
   * behind "+1" on the one day it mattered.
   */
  const isBookingEvent = (event) =>
    (event?.eventKind || event?.event_kind) === 'court_booking';

  /**
   * "+2" was a dead end: it said a day held more than the cell could show and
   * gave no way to see it. Clicking it now opens that day, which is the screen
   * that already lists every event in full with its details.
   */
  const openDay = (date) => {
    setCurrentDate(new Date(date));
    setCalendarView('today');
  };

  /** Bookings before windows; within each, by time. */
  const sortedForCell = (events) => [...events].sort((a, b) => {
    const byKind = Number(isBookingEvent(b)) - Number(isBookingEvent(a));
    if (byKind !== 0) return byKind;
    return String(a.start_time || '').localeCompare(String(b.start_time || ''));
  });

  const KindChip = ({ event }) => {
    if (!isCourt) return null;
    const kinds = {
      court_open:    ['Open for hire', 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'],
      court_block:   ['Closed',        'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'],
      court_booking: ['Booked',        'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'],
    };
    const [label, tone] = kinds[event?.eventKind || event?.event_kind] || [];
    if (!label) return null;
    return (
      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${tone}`}>
        {label}
        {/* A hold a session created is not the court's to release: the session
            owns it, and freeing the hour means changing the session. */}
        {(event?.parentEventId || event?.parent_event_id) ? ' · from a session' : ''}
      </span>
    );
  };
  const [courts, setCourts] = useState([]);
  // What the session being edited currently costs. `level` says which of
  // event/series/programme answered, so the pricing section can warn that
  // attaching a price here overrides an inherited one.
  const [resolvedPrice, setResolvedPrice] = useState({ level: null, prices: [] });
  const { theme } = useTheme();

  // Calendar state
  const [calendarView, setCalendarView] = useState('list'); // today, weekly, monthly, list
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [showImportPdfModal, setShowImportPdfModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
  const [showInviteesModal, setShowInviteesModal] = useState(false);
  // 'future' when opened from a whole series, so the change reaches every upcoming date.
  const [inviteesScope, setInviteesScope] = useState('this');
  // Events in the list whose replies are opened out, by id.
  const [openReplies, setOpenReplies] = useState(() => new Set());
  const toggleReplies = (id) => setOpenReplies((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  // The event being edited in place, by id. Editing happens in the row itself
  // rather than in a dialog: the row already shows everything the old details
  // modal did, and a form inside a modal inside a modal left three stacked
  // Cancels with nothing to say which backed out of what.
  const [inlineEditId, setInlineEditId] = useState(null);
  // Pricing expands inside that form, for the same reason.
  const [showPricing, setShowPricing] = useState(false);
  const [editEvent, setEditEvent] = useState(null);

  useEffect(() => {
    if (!isSession || !academyId) return;
    import('../../services/api').then(({ academyAPI }) =>
      academyAPI.courts.list(academyId)
        .then(res => setCourts(res?.courts || res || []))
        .catch(() => setCourts([])),
    );
  }, [isSession, academyId]);

  // Resolved on entering the edit form so the button can show what the session
  // costs today, whether that price is its own or inherited.
  useEffect(() => {
    if (!isPriced || !inlineEditId || !editEvent?.id) { setResolvedPrice({ level: null, prices: [] }); return; }
    let cancelled = false;
    pricingAPI.forEvent(editEvent.id)
      .then(res => { if (!cancelled) setResolvedPrice(res || { level: null, prices: [] }); })
      .catch(() => { if (!cancelled) setResolvedPrice({ level: null, prices: [] }); });
    return () => { cancelled = true; };
  }, [isPriced, inlineEditId, editEvent?.id]);

  const sessionPrices = resolvedPrice.prices || [];


  // Re-resolved rather than trusting what the panel listed: the panel may have
  // been editing the series, and only the event → series → programme walk knows
  // which level now answers.
  const refreshResolvedPrice = (eventId) => {
    if (!eventId) return;
    pricingAPI.forEvent(eventId)
      .then(res => setResolvedPrice(res || { level: null, prices: [] }))
      .catch(() => {});
  };
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [showResponseDetails, setShowResponseDetails] = useState(false);
  const [listPage, setListPage] = useState(0);
  const LIST_PAGE_SIZE = 10;
  // 'expanded' = every occurrence on its own row, as before. 'compact' = one row
  // per series, opened on demand. Expanded stays the default: it is what the
  // list has always shown, and it is right for a schedule that barely repeats.
  const [listMode, setListMode] = useState('expanded');
  // Which series are open in compact mode, by group key.
  const [openSeries, setOpenSeries] = useState(() => new Set());
  const [scopeModal, setScopeModal] = useState({ open: false, action: null, eventId: null });
  const [seriesConfirm, setSeriesConfirm] = useState({ open: false, pendingArgs: null });
  const [editScope, setEditScope] = useState('this');

  // What the pricing button summarises must be what the panel underneath is
  // editing. They are not always the same thing: the button used to show what
  // the session resolves to while the panel showed the series, so a session
  // priced $15 sat above a panel reading "no prices yet".
  const priceScopeOwner = (editScope !== 'this' && editEvent?.recurrenceRuleId)
    ? { type: 'event_series', id: editEvent.recurrenceRuleId }
    : { type: 'event', id: editEvent?.id };
  const [scopePrices, setScopePrices] = useState([]);

  useEffect(() => {
    if (!isPriced || !inlineEditId || !academyId || !priceScopeOwner.id) { setScopePrices([]); return; }
    let cancelled = false;
    pricingAPI.list(academyId, priceScopeOwner.type, priceScopeOwner.id)
      .then(rows => { if (!cancelled) setScopePrices(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setScopePrices([]); });
    return () => { cancelled = true; };
  }, [isPriced, inlineEditId, academyId, priceScopeOwner.type, priceScopeOwner.id, resolvedPrice]);

  // Events API integration
  // Pass shouldLoad=false because we handle fetching in our own useEffect based on calendar view
  const {
    events,
    loading: eventsLoading,
    fetchEvents,
    refetchCurrent,
    createEvent: createEventAPI,
    updateEvent: updateEventAPI,
    deleteEvent: deleteEventAPI,
    updateEventResponse
  } = useEvents(ownerType, ownerId, false);

  // Fetch events when calendar view, date, or showPastEvents changes
  useEffect(() => {
    if (ownerType && ownerId) {
      setListPage(0);
      const { startDate, endDate } = getDateRangeForView(calendarView, currentDate);
      // For list view, don't pass date range - let backend handle filtering
      // For calendar views, pass the date range
      if (calendarView === 'list') {
        fetchEvents(null, null, showPastEvents);
      } else {
        fetchEvents(startDate, endDate, showPastEvents);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarView, currentDate, ownerType, ownerId, showPastEvents]);

  // Mobile scroll lock for Add Event modal
  useEffect(() => {
    if (showAddEventModal) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showAddEventModal]);

  // Mobile scroll lock for Event Details modal
  useEffect(() => {
    if (showEventDetailsModal) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showEventDetailsModal]);

  const handleAddEvent = async (eventData) => {
    const result = await createEventAPI(eventData);
    if (result.success) {
      toast.success('Event created successfully');
    } else {
      toast.error(`Failed to create event: ${result.error}`);
      throw new Error(result.error);
    }
  };

  const handleImportFromPdf = async (events) => {
    const results = await Promise.allSettled(events.map(e => createEventAPI(e)));
    const failed = results.filter(r => r.status === 'rejected').length;
    if (failed > 0) {
      throw new Error(`${failed} event(s) failed to create`);
    }
  };

  const openEditForm = (event, scope = 'this') => {
    const eventDate = getDateFromTimestamp(event.start_time);
    const eventStartTime = getTimeFromTimestamp(event.start_time);
    const eventEndTime = getTimeFromTimestamp(event.end_time);
    setEditScope(scope);
    setEditEvent({
      id: event.id,
      recurrenceRuleId: event.recurrenceRuleId || null,
      // The pattern itself, editable when the change applies to the series.
      // Until now it was fixed at creation: "All in series" could move the
      // time and the price but never the days, so "we are moving open play to
      // Wednesdays" meant deleting the series and building it again.
      recurrence: event.recurrenceRule ? {
        frequency:  event.recurrenceRule.frequency || 'weekly',
        interval:   event.recurrenceRule.interval || 1,
        // Already an array by the time it reaches here — the API parses the
        // stored JSON before sending it. Kept tolerant of the raw string form
        // so this does not depend on which end does the parsing.
        daysOfWeek: (() => {
          const d = event.recurrenceRule.daysOfWeek ?? event.recurrenceRule.days_of_week;
          if (Array.isArray(d)) return d;
          try { return JSON.parse(d || '[]'); } catch { return []; }
        })(),
        endType:    event.recurrenceRule.endType || event.recurrenceRule.end_type || 'count',
        endCount:   event.recurrenceRule.endCount ?? event.recurrenceRule.end_count ?? 12,
        endDate:    (event.recurrenceRule.endDate || event.recurrenceRule.end_date || '').slice(0, 10),
      } : null,
      subject: event.subject,
      locationName: event.venue?.name || '',
      address: event.venue?.fullAddress || '',
      placeId: event.venue?.placeId || '',
      lat: event.venue?.lat || null,
      lng: event.venue?.lng || null,
      city: event.venue?.city || '',
      state: event.venue?.state || '',
      country: event.venue?.country || '',
      zipCode: event.venue?.zipCode || '',
      date: eventDate,
      startTime: eventStartTime,
      endTime: eventEndTime,
      // Session details, carried into the edit form so saving does not have to
      // resend them blind. Absent on a squad event, where they mean nothing.
      capacity: event.capacity ?? '',
      courtIds: Array.isArray(event.courtIds) ? event.courtIds
        : (Array.isArray(event.court_ids) ? event.court_ids : []),
    });
    setInlineEditId(event.id);
    setShowPricing(false);
    // Editing lives in the list, so leave whatever view asked for it.
    setShowEventDetailsModal(false);
    setCalendarView('list');
  };

  // Mirrors AddEventModal's handler: a picked place carries the geocoded
  // fields the venue needs, not just a display string.
  const handleEditAddressSelect = (addressData) => {
    setEditEvent(prev => ({
      ...prev,
      locationName: addressData.placeName || addressData.landmark || prev.locationName || '',
      address:      addressData.fullAddress || '',
      placeId:      addressData.placeId || '',
      lat:          addressData.coordinates?.lat || null,
      lng:          addressData.coordinates?.lng || null,
      city:         addressData.city || '',
      state:        addressData.state || '',
      country:      addressData.country || '',
      zipCode:      addressData.zipCode || ''
    }));
  };

  const handleSaveEvent = async () => {
    if (!editEvent) return;

    if (!editEvent.subject.trim()) {
      toast.error('Please fill in the event subject');
      return;
    }

    if (editEvent.startTime >= editEvent.endTime) {
      toast.error('End time must be after start time');
      return;
    }

    // Pass date/time directly - the useEvents hook handles timezone conversion
    const eventData = {
      subject:      editEvent.subject,
      locationName: editEvent.locationName,
      address:      editEvent.address,
      placeId:      editEvent.placeId,
      lat:          editEvent.lat,
      lng:          editEvent.lng,
      city:         editEvent.city,
      state:        editEvent.state,
      country:      editEvent.country,
      zipCode:      editEvent.zipCode,
      date:         editEvent.date,
      startTime:    editEvent.startTime,
      endTime:      editEvent.endTime,
      ...(isSession ? {
        capacity: editEvent.capacity === '' ? null : parseInt(editEvent.capacity, 10),
        courtIds: editEvent.courtIds || [],
      } : {}),
      // Only with the series in scope: reshaping one occurrence makes no sense,
      // and the server would refuse it.
      ...(editScope !== 'this' && editEvent.recurrence ? { recurrence: editEvent.recurrence } : {}),
    };

    const result = await updateEventAPI(editEvent.id, eventData, editScope);

    if (result.success) {
      const r = result.data?.reshaped || result.reshaped;
      if (r && (r.added || r.removed || r.keptBooked)) {
        const parts = [];
        if (r.added)   parts.push(`${r.added} added`);
        if (r.removed) parts.push(`${r.removed} removed`);
        toast.success(`Series updated — ${parts.join(', ') || 'no date changes'}`);
        // Said separately and not as a success: a night that survived the new
        // pattern because somebody had booked it is something to go and deal
        // with, not a detail.
        if (r.keptBooked) {
          toast(`${r.keptBooked} booked ${r.keptBooked === 1 ? 'date was' : 'dates were'} kept — cancel ${r.keptBooked === 1 ? 'it' : 'them'} yourself if they must go`,
            { icon: '⚠️', duration: 6000 });
        }
      } else {
        toast.success('Event updated successfully');
      }
      setInlineEditId(null);
      setEditEvent(null);
      if (editScope !== 'this') { fetchEvents(); return; }
      // Adopt the row the list now holds. Patching a few fields by hand here
      // left out the date and times, so rescheduling a single event reported
      // success while the open modal kept showing the old day and hour.
      if (result.data) setSelectedEvent(prev => ({ ...prev, ...result.data }));
    } else {
      toast.error(`Failed to update event: ${result.error}`);
    }
  };

  const handleCancelEdit = () => {
    setInlineEditId(null);
    setEditEvent(null);
    setEditScope('this');
  };

  const handleDeleteEvent = async (scope = 'this') => {
    if (!selectedEvent) return;

    const result = await deleteEventAPI(selectedEvent.id, scope);

    if (result.success) {
      toast.success('Event deleted successfully');
      setShowDeleteConfirm(false);
      setShowEventDetailsModal(false);
      setSelectedEvent(null);
    } else {
      toast.error(`Failed to delete event: ${result.error}`);
    }
  };

  const handleDeleteEventClick = () => {
    if (selectedEvent?.recurrenceRuleId) {
      setScopeModal({ open: true, action: 'delete', eventId: selectedEvent.id });
    } else {
      setShowDeleteConfirm(true);
    }
  };

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const handleDeleteEventFromList = async (eventId, scope = 'this') => {
    const result = await deleteEventAPI(eventId, scope);
    setConfirmDeleteId(null);
    if (result.success) {
      toast.success('Event deleted successfully');
    } else {
      toast.error(`Failed to delete event: ${result.error}`);
    }
  };

  const handleDeleteFromListClick = (event) => {
    if (event.recurrenceRuleId) {
      setScopeModal({ open: true, action: 'delete', eventId: event.id });
    } else {
      setConfirmDeleteId(event.id);
    }
  };

  /**
   * The edit form, rendered inside whichever row is being edited.
   *
   * A function rather than a component so it closes over editEvent and its
   * setter: declaring a component inside this one would give it a new identity
   * every render, and React would remount the inputs on each keystroke.
   */
  const renderEditForm = () => {
    const field = `w-full px-4 py-2 rounded-lg ${theme.border} border ${theme.card} ${theme.text}`;
    return (
      <div className={`space-y-4 p-4 rounded-lg border-2 ${theme.borderAccent} ${theme.backgroundSecondary}`}>
        {/* Which events the save lands on. Shown rather than asked: the pencil
            that was clicked already implies an answer, and a dialog in front of
            the form made every edit a two-step affair for a choice most people
            never change. */}
        {editEvent.recurrenceRuleId && (
          <div>
            <label className={`block text-xs font-medium ${theme.textMuted} mb-1.5`}>Changes apply to</label>
            <div className={`inline-flex flex-wrap rounded-lg border ${theme.border} p-0.5`}>
              {[
                { value: 'this',      label: 'This event' },
                { value: 'following', label: 'This and following' },
                { value: 'all',       label: 'All in series' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setEditScope(opt.value)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    editScope === opt.value ? theme.tabActive : `${theme.textMuted} ${theme.cardHover}`
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className={`block text-sm font-medium ${theme.text} mb-2`}>Event Subject *</label>
          <input
            type="text"
            value={editEvent.subject}
            onChange={(e) => setEditEvent(prev => ({ ...prev, subject: e.target.value }))}
            className={field}
          />
        </div>

        <div>
          <label className={`block text-sm font-medium ${theme.text} mb-2`}>Venue Name</label>
          <input
            type="text"
            value={editEvent.locationName}
            onChange={(e) => setEditEvent(prev => ({ ...prev, locationName: e.target.value }))}
            placeholder="e.g., Parsippany Sports Complex"
            className={field}
          />
        </div>

        <div>
          <label className={`block text-sm font-medium ${theme.text} mb-2`}>Address</label>
          {/* Same picker the create form uses. A free-text box here let the
              address change while place id and coordinates kept their old
              values, so Navigate went on pointing at the previous venue. */}
          <GooglePlacesAutocomplete
            googleApiKey={getConfig('GOOGLE_PLACES_API_KEY')}
            placeholder="Search for address..."
            theme={theme}
            initialValue={editEvent.address}
            onAddressSelect={handleEditAddressSelect}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-2`}>Date *</label>
            <input
              type="date"
              value={editEvent.date}
              onChange={(e) => setEditEvent(prev => ({ ...prev, date: e.target.value }))}
              className={field}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-2`}>Start (ET) *</label>
            <input
              type="time"
              value={editEvent.startTime}
              onChange={(e) => setEditEvent(prev => ({ ...prev, startTime: e.target.value }))}
              className={field}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-2`}>End (ET) *</label>
            <input
              type="time"
              value={editEvent.endTime}
              onChange={(e) => setEditEvent(prev => ({ ...prev, endTime: e.target.value }))}
              className={field}
            />
          </div>
        </div>

        {/* The pattern, editable only when the change applies to the series.
            Reshaping from a single occurrence would be ambiguous — and the
            server refuses it — so the controls are simply not offered there. */}
        {editScope !== 'this' && editEvent.recurrence && (
          <div className={`p-3 rounded-lg border ${theme.border} space-y-3`}>
            <p className={`text-sm font-medium ${theme.text}`}>Repeats</p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`block text-xs ${theme.textSecondary} mb-1`}>How often</label>
                <select
                  value={editEvent.recurrence.frequency}
                  onChange={(e) => setEditEvent(prev => ({
                    ...prev, recurrence: { ...prev.recurrence, frequency: e.target.value },
                  }))}
                  className={field}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div>
                <label className={`block text-xs ${theme.textSecondary} mb-1`}>Every</label>
                <input
                  type="number" min="1"
                  value={editEvent.recurrence.interval}
                  onChange={(e) => setEditEvent(prev => ({
                    ...prev, recurrence: { ...prev.recurrence, interval: parseInt(e.target.value, 10) || 1 },
                  }))}
                  className={field}
                />
              </div>
            </div>

            {editEvent.recurrence.frequency === 'weekly' && (
              <div>
                <label className={`block text-xs ${theme.textSecondary} mb-1.5`}>On these days</label>
                <div className="flex flex-wrap gap-1.5">
                  {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(d => {
                    const on = (editEvent.recurrence.daysOfWeek || []).includes(d);
                    return (
                      <button key={d} type="button"
                        onClick={() => setEditEvent(prev => ({
                          ...prev,
                          recurrence: {
                            ...prev.recurrence,
                            daysOfWeek: on
                              ? prev.recurrence.daysOfWeek.filter(x => x !== d)
                              : [...(prev.recurrence.daysOfWeek || []), d],
                          },
                        }))}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                          on ? `${theme.tabActive} border-transparent` : `${theme.border} ${theme.textSecondary}`}`}>
                        {d[0] + d.slice(1).toLowerCase()}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`block text-xs ${theme.textSecondary} mb-1`}>Until</label>
                <select
                  value={editEvent.recurrence.endType}
                  onChange={(e) => setEditEvent(prev => ({
                    ...prev, recurrence: { ...prev.recurrence, endType: e.target.value },
                  }))}
                  className={field}
                >
                  <option value="count">A number of times</option>
                  <option value="date">A date</option>
                </select>
              </div>
              <div>
                {editEvent.recurrence.endType === 'count' ? (
                  <>
                    <label className={`block text-xs ${theme.textSecondary} mb-1`}>How many</label>
                    <input
                      type="number" min="1"
                      value={editEvent.recurrence.endCount ?? ''}
                      onChange={(e) => setEditEvent(prev => ({
                        ...prev, recurrence: { ...prev.recurrence, endCount: parseInt(e.target.value, 10) || 1 },
                      }))}
                      className={field}
                    />
                  </>
                ) : (
                  <>
                    <label className={`block text-xs ${theme.textSecondary} mb-1`}>Last date</label>
                    <input
                      type="date"
                      value={editEvent.recurrence.endDate || ''}
                      onChange={(e) => setEditEvent(prev => ({
                        ...prev, recurrence: { ...prev.recurrence, endDate: e.target.value },
                      }))}
                      className={field}
                    />
                  </>
                )}
              </div>
            </div>

            <p className={`text-xs ${theme.textMuted}`}>
              Saving adds the dates this pattern gains and removes the ones it loses.
              Dates somebody has already booked are kept.
            </p>
          </div>
        )}

        {/* Session details — same fields as creating one, so an offering's
            night can be corrected without deleting it. */}
        {isPriced && (
          <div className={`p-3 rounded-lg border ${theme.border} space-y-3`}>
            {/* Spots and courts belong to a session. A court's open window has
                neither: it is one court, hired whole, by the hour. Pricing below
                applies to both. */}
            {isSession && (
            <div>
              <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>
                <Users className="w-4 h-4 inline mr-1" />
                Spots <span className={`font-normal ${theme.textSecondary}`}>(optional)</span>
              </label>
              <input
                type="number" min="1"
                value={editEvent.capacity ?? ''}
                onChange={(e) => setEditEvent(prev => ({ ...prev, capacity: e.target.value }))}
                placeholder="Leave blank for unlimited"
                className={field}
              />
            </div>
            )}

            {isSession && courts.length > 0 && (
              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Courts <span className={`font-normal ${theme.textSecondary}`}>(optional)</span>
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {courts.map(c => {
                    const on = (editEvent.courtIds || []).includes(c.id);
                    return (
                      <button key={c.id} type="button"
                        onClick={() => setEditEvent(prev => ({
                          ...prev,
                          courtIds: on
                            ? (prev.courtIds || []).filter(id => id !== c.id)
                            : [...(prev.courtIds || []), c.id],
                        }))}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                          on ? `${theme.tabActive} border-transparent` : `${theme.border} ${theme.textSecondary}`}`}>
                        {c.name}
                      </button>
                    );
                  })}
                </div>
                <p className={`text-xs ${theme.textSecondary} mt-1`}>
                  Changing these releases the old courts and holds the new ones.
                </p>
              </div>
            )}

            <div>
              <button
                type="button"
                onClick={() => {
                  // Collapsing re-resolves: what the panel just changed may
                  // have moved which level answers.
                  if (showPricing) refreshResolvedPrice(editEvent.id);
                  setShowPricing(v => !v);
                }}
                className={`w-full py-2 rounded-lg text-sm font-medium ${theme.borderAccent} border ${theme.link} ${theme.cardHover} transition-colors flex items-center justify-center gap-1.5`}
              >
                <DollarSign className="w-4 h-4" />
                {/* What this costs TODAY, before anything is opened.
                    The label used to read the prices attached at this exact
                    scope and nothing else, so an hour charging the court's
                    default — or a session inheriting its series' price — said
                    "Attach Price" and showed no number at all, which reads as
                    free. An inherited rate is still the rate; it just says
                    where it comes from. */}
                {scopePrices.length > 0
                  ? `Pricing — ${scopePrices.map(p => formatMoney(p.amount, p.currency) + (isCourt ? '/hour' : '')).join(' · ')}`
                  : sessionPrices.length > 0
                    ? `Pricing — ${formatMoney(sessionPrices[0].amount, sessionPrices[0].currency)}`
                      + `${isCourt ? '/hour' : ''} ${INHERITED_FROM[resolvedPrice.level] || '(inherited)'}`
                    : 'Attach Price'}
                <ChevronRight className={`w-4 h-4 transition-transform ${showPricing ? 'rotate-90' : ''}`} />
              </button>

              {showPricing && (() => {
                // The scope was already chosen above, so the panel does not ask
                // again — it prices whatever the save is going to land on.
                // `seriesId` is deliberately not passed: that is what draws the
                // panel's own This session / Whole series tabs.
                const wholeSeries = editScope !== 'this' && editEvent.recurrenceRuleId;
                return (
                  <div className={`mt-3 pt-3 border-t ${theme.border}`}>
                    <p className={`text-xs ${theme.textMuted} mb-2`}>
                      {wholeSeries
                        ? 'Pricing every session in this series.'
                        : 'Pricing this session only.'}
                    </p>
                    <PricingPanel
                      academyId={academyId}
                      ownerType={wholeSeries ? 'event_series' : 'event'}
                      ownerId={wholeSeries ? editEvent.recurrenceRuleId : editEvent.id}
                      entityName={editEvent.subject}
                      inherited={resolvedPrice}
                      onPricesChange={(list, { afterWrite } = {}) => {
                        setScopePrices(list);
                        // The rows carry the prices they were fetched with, so
                        // a price attached here would not show on the session
                        // until something else refetched. Only on a real write:
                        // the panel also reports its list on every open.
                        if (afterWrite) {
                          refreshResolvedPrice(editEvent.id);
                          refetchCurrent();
                        }
                      }}
                    />
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleCancelEdit}
            className={`${btn.base} ${btn.md} ${theme.buttonGhost} flex-1`}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveEvent}
            className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex-1 flex items-center justify-center gap-2`}
          >
            <Save className="w-4 h-4" />
            Save Changes
          </button>
        </div>
      </div>
    );
  };

  const formatRecurrenceDescription = (rule, index) => {
    if (!rule) return null;
    const { frequency, interval = 1, daysOfWeek, endType, endCount, endDate } = rule;
    const freq = interval === 1
      ? frequency               // "daily" / "weekly" / "monthly"
      : `every ${interval} ${frequency === 'daily' ? 'days' : frequency === 'weekly' ? 'weeks' : 'months'}`;
    const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const days = daysOfWeek?.length
      ? ' on ' + daysOfWeek.map(d => (typeof d === 'number' ? dayNames[d] : d)).join(', ')
      : '';
    const end = endType === 'count' && endCount
      ? `, ${endCount} event${endCount !== 1 ? 's' : ''} total`
      : endType === 'date' && endDate
        ? `, until ${new Date(endDate).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}`
        : '';
    const occurrence = index != null ? ` · #${index + 1}` : '';
    return `Repeats ${freq}${days}${end}${occurrence}`;
  };

  const generateArrivalSlots = (event) => {
    if (!event?.start_time) return [];
    const start = new Date(event.start_time);
    const slots = [];
    for (let i = 0; i < 3; i++) {
      slots.push(new Date(start.getTime() + i * 30 * 60 * 1000));
    }
    return slots;
  };

  const formatSlotTime = (date) =>
    new Date(date).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });

  const buildSlotCounts = (slots, responses) => {
    const counts = {};
    slots.forEach(s => { counts[s.toISOString()] = 0; });
    (responses || []).forEach(r => {
      if (!r.arrivalTime) return;
      const ms = new Date(r.arrivalTime).getTime();
      const nearest = slots.reduce((b, s) =>
        Math.abs(s.getTime() - ms) < Math.abs(b.getTime() - ms) ? s : b
      );
      counts[nearest.toISOString()]++;
    });
    return counts;
  };

  const nearestSlotISO = (arrivalTime, slots) => {
    if (!arrivalTime || slots.length === 0) return null;
    const ms = new Date(arrivalTime).getTime();
    return slots.reduce((b, s) =>
      Math.abs(s.getTime() - ms) < Math.abs(b.getTime() - ms) ? s : b
    ).toISOString();
  };

  const handleUpdateAvailability = async (eventId, status, arrivalTime = null, applyToSeries = false) => {
    const currentUser = getCurrentUser();
    if (!currentUser) return;
    const result = await updateEventResponse(eventId, status, null, arrivalTime, applyToSeries);
    if (!result.success) {
      toast.error(`Failed to update availability: ${result.error}`);
    } else {
      if (onEventResponseUpdate) onEventResponseUpdate(eventId, status);
    }
  };

  const handleAvailabilityClick = (event, status, arrivalTime = null) => {
    if (event.recurrenceRuleId) {
      setSeriesConfirm({ open: true, pendingArgs: { eventId: event.id, status, arrivalTime } });
    } else {
      handleUpdateAvailability(event.id, status, arrivalTime);
    }
  };

  const handleArrivalTimeSelect = (event, isoTime) => {
    const status = new Date(isoTime).getTime() <= new Date(event.start_time).getTime()
      ? 'available' : 'join-late';
    handleAvailabilityClick(event, status, isoTime);
  };

  const isEventPast = (event) => {
    if (!event) return false;
    if (event.status === 'completed' || event.status === 'cancelled') return true;
    if (!event.end_time) return false;
    return new Date(event.end_time) < new Date() && event.status !== 'in_progress';
  };

  const getUserResponseForEvent = (event) => {
    const currentUser = getCurrentUser();
    if (!currentUser || !event.responses) return null;
    return event.responses.find(r => r.userId === currentUser.id);
  };

  const getResponseCounts = (event) => {
    if (!event.responses) return { available: 0, notAvailable: 0, maybe: 0, joinLate: 0, invited: 0 };
    return {
      available: event.responses.filter(r => r.status === 'available').length,
      notAvailable: event.responses.filter(r => r.status === 'not-available').length,
      maybe: event.responses.filter(r => r.status === 'maybe').length,
      joinLate: event.responses.filter(r => r.status === 'join-late').length,
      invited: event.responses.filter(r => r.status === 'invited').length
    };
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventDetailsModal(true);
  };

  const handlePrevPeriod = () => {
    const newDate = new Date(currentDate);
    if (calendarView === 'today') {
      newDate.setDate(newDate.getDate() - 1);
    } else if (calendarView === 'weekly') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNextPeriod = () => {
    const newDate = new Date(currentDate);
    if (calendarView === 'today') {
      newDate.setDate(newDate.getDate() + 1);
    } else if (calendarView === 'weekly') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleRefreshEvents = () => {
    const { startDate, endDate } = getDateRangeForView(calendarView, currentDate);
    fetchEvents(startDate, endDate);
  };

  const getWeekDays = () => {
    const start = new Date(currentDate);
    const day = start.getDay();
    const diff = start.getDate() - day;
    start.setDate(diff);

    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      return date;
    });
  };

  const getMonthDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  // Get all events sorted by date ascending for list view
  const getSortedEventsList = () => {
    return [...events].sort((a, b) => {
      // Sort by start_time timestamp
      const aTime = new Date(a.start_time).getTime();
      const bTime = new Date(b.start_time).getTime();
      return aTime - bTime;
    });
  };

  /**
   * The list collapsed to one entry per series.
   *
   * A weekly open play booked out for a term is sixty near-identical rows, and
   * scrolling past them is the only way to reach the next thing. Grouped, it is
   * one line that opens.
   *
   * Keyed on the recurrence rule rather than the subject: two series can share
   * a name, and an occurrence edited to a different subject still belongs to
   * its series. Events with no rule stay as groups of one, so a mixed list
   * needs no special case.
   */
  const getGroupedEventsList = () => {
    const groups = new Map();
    for (const event of getSortedEventsList()) {
      const key = event.recurrenceRuleId ? `series-${event.recurrenceRuleId}` : `one-${event.id}`;
      if (!groups.has(key)) {
        groups.set(key, { key, seriesId: event.recurrenceRuleId || null, events: [] });
      }
      groups.get(key).events.push(event);
    }
    return [...groups.values()];
  };

  // What a page of the list holds: occurrences when expanded, series when
  // compact. Pagination counts these, so a page is always a screenful either way.
  const getListUnits = () => (listMode === 'compact' ? getGroupedEventsList() : getSortedEventsList());

  const getEventsForDate = (date) => {
    if (!date) return [];
    // Format the calendar date as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    // Filter events by their start_time date (in local timezone)
    return events.filter(event => {
      const eventDate = getDateFromTimestamp(event.start_time);
      return eventDate === dateStr;
    });
  };

  const getDateRangeForView = (view, date) => {
    // Helper function to format date as YYYY-MM-DD in local timezone
    const formatLocalDate = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    let startDate, endDate;

    if (view === 'today') {
      startDate = endDate = formatLocalDate(date);
    } else if (view === 'weekly') {
      const start = new Date(date);
      const day = start.getDay();
      const diff = start.getDate() - day; // Get Sunday
      start.setDate(diff);

      const end = new Date(start);
      end.setDate(start.getDate() + 6); // Get Saturday

      startDate = formatLocalDate(start);
      endDate = formatLocalDate(end);
    } else if (view === 'list') {
      // For list view, get all events from today onwards (next 6 months)
      const start = new Date();
      const end = new Date();
      end.setMonth(end.getMonth() + 6);

      startDate = formatLocalDate(start);
      endDate = formatLocalDate(end);
    } else { // monthly
      const year = date.getFullYear();
      const month = date.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      startDate = formatLocalDate(firstDay);
      endDate = formatLocalDate(lastDay);
    }

    return { startDate, endDate };
  };

  return (
    <div className="space-y-4">
      {/* Schedule Header */}
      <div className={`${theme.card} rounded-2xl border ${theme.border} p-4`}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <div className="flex items-center gap-x-4">
            {calendarView !== 'list' && (
              <>
                <button
                  onClick={handlePrevPeriod}
                  className={`p-2 rounded-lg ${theme.cardHover} transition-colors`}
                >
                  <ChevronLeft className={`w-5 h-5 ${theme.text}`} />
                </button>
                <h3 className={`text-lg font-semibold ${theme.text}`}>
                  {calendarView === 'today' && currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  {calendarView === 'weekly' && `Week of ${getWeekDays()[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                  {calendarView === 'monthly' && currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h3>
                <button
                  onClick={handleNextPeriod}
                  className={`p-2 rounded-lg ${theme.cardHover} transition-colors`}
                >
                  <ChevronRight className={`w-5 h-5 ${theme.text}`} />
                </button>
              </>
            )}
            {calendarView === 'list' && (
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className={`text-lg font-semibold ${theme.text}`}>
                  {showPastEvents ? 'All Events' : 'Upcoming Events'}
                </h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showPastEvents}
                    onChange={(e) => setShowPastEvents(e.target.checked)}
                    className={`w-4 h-4 rounded border-gray-300 ${theme.link} focus:ring-current`}
                  />
                  <span className={`text-sm ${theme.textMuted}`}>Show past</span>
                </label>
                <div className={`inline-flex rounded-lg border ${theme.border} p-0.5`}>
                  {[
                    { value: 'expanded', label: 'Expanded' },
                    { value: 'compact',  label: 'By series' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      // Page resets: page 4 of occurrences is well past the end
                      // of the same list grouped into series.
                      onClick={() => { setListMode(opt.value); setListPage(0); }}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                        listMode === opt.value ? theme.tabActive : `${theme.textMuted} ${theme.cardHover}`
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {getListUnits().length > LIST_PAGE_SIZE && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setListPage(p => p - 1)}
                      disabled={listPage === 0}
                      className={`p-1 rounded border ${theme.border} transition-all ${listPage === 0 ? 'opacity-30 cursor-not-allowed' : theme.cardHover}`}
                    >
                      <ChevronLeft className={`w-4 h-4 ${theme.text}`} />
                    </button>
                    <span className={`text-sm ${theme.textMuted}`}>
                      {listPage + 1} / {Math.ceil(getListUnits().length / LIST_PAGE_SIZE)}
                    </span>
                    <button
                      onClick={() => setListPage(p => p + 1)}
                      disabled={(listPage + 1) * LIST_PAGE_SIZE >= getListUnits().length}
                      className={`p-1 rounded border ${theme.border} transition-all ${(listPage + 1) * LIST_PAGE_SIZE >= getListUnits().length ? 'opacity-30 cursor-not-allowed' : theme.cardHover}`}
                    >
                      <ChevronRight className={`w-4 h-4 ${theme.text}`} />
                    </button>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={handleRefreshEvents}
              className={`p-2 rounded-lg ${theme.cardHover} transition-colors hover:rotate-180 hover:scale-110`}
              title="Refresh events"
            >
              <RefreshCw className={`w-5 h-5 ${theme.text} ${eventsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* View Switcher */}
            <div className={`flex gap-x-1 ${theme.backgroundSecondary} rounded-lg p-1`}>
              <button
                onClick={() => setCalendarView('today')}
                className={`px-2 sm:px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  calendarView === 'today'
                    ? `${theme.card} shadow-sm ${theme.text}`
                    : `${theme.textSecondary} hover:${theme.backgroundSecondary}`
                }`}
              >
                Today
              </button>
              <button
                onClick={() => setCalendarView('weekly')}
                className={`px-2 sm:px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  calendarView === 'weekly'
                    ? `${theme.card} shadow-sm ${theme.text}`
                    : `${theme.textSecondary} hover:${theme.backgroundSecondary}`
                }`}
              >
                Week
              </button>
              <button
                onClick={() => setCalendarView('monthly')}
                className={`px-2 sm:px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  calendarView === 'monthly'
                    ? `${theme.card} shadow-sm ${theme.text}`
                    : `${theme.textSecondary} hover:${theme.backgroundSecondary}`
                }`}
              >
                Month
              </button>
              <button
                onClick={() => setCalendarView('list')}
                className={`px-2 sm:px-3 py-1 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
                  calendarView === 'list'
                    ? `${theme.card} shadow-sm ${theme.text}`
                    : `${theme.textSecondary} hover:${theme.backgroundSecondary}`
                }`}
              >
                <List className={`w-4 h-4`} />
                <span className="hidden sm:inline">List</span>
              </button>
            </div>
            {isMember && (
              <>
                <button
                  onClick={() => setShowImportPdfModal(true)}
                  className={`${btn.base} ${btn.md} ${theme.buttonGhost} flex items-center gap-x-1`}
                  title="Import events from PDF permit"
                >
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline">Import PDF</span>
                </button>
                <button
                  onClick={() => setShowAddEventModal(true)}
                  className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-x-1`}
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Add Event</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Calendar Views */}
        {calendarView === 'today' && (
          <div className="space-y-3">
            {getEventsForDate(currentDate).length > 0 ? (
              getEventsForDate(currentDate).map(event => {
                const userResponse = getUserResponseForEvent(event);
                const counts = getResponseCounts(event);
                return (
                  <div key={event.id} className={`p-4 rounded-lg ${theme.backgroundSecondary} border-l-4 ${theme.borderAccent}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className={`font-semibold ${theme.text}`}>{event.subject}</h4>
                          <KindChip event={event} />
                        </div>
                        {event.recurrenceRuleId && (
                          <span className={`inline-flex items-center gap-1 text-xs ${theme.link} mb-1`}>
                            <RefreshCw className="w-3 h-3" />
                            Recurring · {event.recurrenceRule?.frequency || 'series'}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => handleEventClick(event)}
                        className={`${theme.link} ${theme.linkHover} text-sm font-medium`}
                      >
                        View Details
                      </button>
                    </div>
                    <div className="space-y-2 text-sm mb-3">
                      <div className="flex items-center gap-x-2">
                        <Clock className={`w-4 h-4 ${theme.textMuted}`} />
                        <span className={theme.textMuted}>
                          {event.start_time_display} - {event.end_time_display}
                        </span>
                      </div>
                      <div className="flex items-center gap-x-2">
                        <MapPin className={`w-4 h-4 ${theme.textMuted}`} />
                        <div className="min-w-0">
                          {event.venue?.name && (
                            <p className={`text-xs font-medium ${theme.textMuted}`}>{event.venue.name}</p>
                          )}
                          <p className={`text-xs ${theme.textMuted}`}>{event.venue?.fullAddress}</p>
                        </div>
                        {event.venue?.fullAddress && (
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venue.fullAddress)}${event.venue?.placeId ? `&query_place_id=${event.venue.placeId}` : ''}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center gap-x-1 text-xs ${theme.link} ${theme.linkHover} flex-shrink-0`}
                            title="Open in Google Maps"
                          >
                            <Navigation className="w-3 h-3" />
                            <span>Navigate</span>
                          </a>
                        )}
                      </div>
                      <SessionMeta event={event} courts={courts} theme={theme} compact unit={isCourt ? '/hour' : null} />
                    </div>

                    {/* Response Counts and Preview */}
                    <div className="mb-3">
                      <div className="flex items-center gap-x-3 mb-2 text-xs">
                        <div className="flex items-center gap-x-1">
                          <Check className="w-3 h-3 text-green-600" />
                          <span className={theme.textMuted}>{counts.available}</span>
                        </div>
                        <div className="flex items-center gap-x-1">
                          <X className="w-3 h-3 text-red-600" />
                          <span className={theme.textMuted}>{counts.notAvailable}</span>
                        </div>
                        <div className="flex items-center gap-x-1">
                          <HelpCircle className="w-3 h-3 text-yellow-600" />
                          <span className={theme.textMuted}>{counts.maybe}</span>
                        </div>
                        <div className="flex items-center gap-x-1">
                          <Clock className="w-3 h-3 text-blue-600" />
                          <span className={theme.textMuted}>{counts.joinLate}</span>
                        </div>
                      </div>

                      {/* Member Names Preview */}
                      {event.responses && event.responses.length > 0 && (
                        <div className="space-y-1">
                          {event.responses.filter(r => r.status === 'available').length > 0 && (
                            <div className="flex items-start gap-x-2">
                              <Check className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                              <div className="flex flex-wrap gap-1 text-xs">
                                {event.responses.filter(r => r.status === 'available').slice(0, 3).map((r, i) => (
                                  <span key={r.userId} className="text-green-700 dark:text-green-400">
                                    {r.username}{i < Math.min(2, event.responses.filter(rr => rr.status === 'available').length - 1) ? ',' : ''}
                                  </span>
                                ))}
                                {event.responses.filter(r => r.status === 'available').length > 3 && (
                                  <span className={theme.textMuted}>+{event.responses.filter(r => r.status === 'available').length - 3}</span>
                                )}
                              </div>
                            </div>
                          )}
                          {event.responses.filter(r => r.status === 'not-available').length > 0 && (
                            <div className="flex items-start gap-x-2">
                              <X className="w-3 h-3 text-red-600 mt-0.5 flex-shrink-0" />
                              <div className="flex flex-wrap gap-1 text-xs">
                                {event.responses.filter(r => r.status === 'not-available').slice(0, 3).map((r, i) => (
                                  <span key={r.userId} className="text-red-700 dark:text-red-400">
                                    {r.username}{i < Math.min(2, event.responses.filter(rr => rr.status === 'not-available').length - 1) ? ',' : ''}
                                  </span>
                                ))}
                                {event.responses.filter(r => r.status === 'not-available').length > 3 && (
                                  <span className={theme.textMuted}>+{event.responses.filter(r => r.status === 'not-available').length - 3}</span>
                                )}
                              </div>
                            </div>
                          )}
                          {event.responses.filter(r => r.status === 'maybe').length > 0 && (
                            <div className="flex items-start gap-x-2">
                              <HelpCircle className="w-3 h-3 text-yellow-600 mt-0.5 flex-shrink-0" />
                              <div className="flex flex-wrap gap-1 text-xs">
                                {event.responses.filter(r => r.status === 'maybe').slice(0, 3).map((r, i) => (
                                  <span key={r.userId} className="text-yellow-700 dark:text-yellow-400">
                                    {r.username}{i < Math.min(2, event.responses.filter(rr => rr.status === 'maybe').length - 1) ? ',' : ''}
                                  </span>
                                ))}
                                {event.responses.filter(r => r.status === 'maybe').length > 3 && (
                                  <span className={theme.textMuted}>+{event.responses.filter(r => r.status === 'maybe').length - 3}</span>
                                )}
                              </div>
                            </div>
                          )}
                          {event.responses.filter(r => r.status === 'join-late').length > 0 && (
                            <div className="flex items-start gap-x-2">
                              <Clock className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
                              <div className="flex flex-wrap gap-1 text-xs">
                                {event.responses.filter(r => r.status === 'join-late').slice(0, 3).map((r, i) => (
                                  <span key={r.userId} className="text-blue-700 dark:text-blue-400">
                                    {r.username}{i < Math.min(2, event.responses.filter(rr => rr.status === 'join-late').length - 1) ? ',' : ''}
                                  </span>
                                ))}
                                {event.responses.filter(r => r.status === 'join-late').length > 3 && (
                                  <span className={theme.textMuted}>+{event.responses.filter(r => r.status === 'join-late').length - 3}</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Availability Buttons */}
                    {allowResponses && isMember && !isEventPast(event) && (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <button
                            onClick={() => handleAvailabilityClick(event, 'available')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              userResponse?.status === 'available'
                                ? 'bg-green-500 text-white'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                          >
                            <Check className="w-4 h-4 inline mr-1" />
                            Available
                          </button>
                          <button
                            onClick={() => handleAvailabilityClick(event, 'not-available')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              userResponse?.status === 'not-available'
                                ? 'bg-red-500 text-white'
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                            }`}
                          >
                            <X className="w-4 h-4 inline mr-1" />
                            Not Available
                          </button>
                          <button
                            onClick={() => handleAvailabilityClick(event, 'maybe')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              userResponse?.status === 'maybe'
                                ? 'bg-yellow-500 text-white'
                                : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                            }`}
                          >
                            <HelpCircle className="w-4 h-4 inline mr-1" />
                            Maybe
                          </button>
                          <button
                            onClick={() => handleAvailabilityClick(event, 'join-late')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              userResponse?.status === 'join-late'
                                ? 'bg-blue-500 text-white'
                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            }`}
                          >
                            <Clock className="w-4 h-4 inline mr-1" />
                            Join Late
                          </button>
                        </div>
                        {event.start_time && (() => {
                          const slots = generateArrivalSlots(event);
                          const slotCounts = buildSlotCounts(slots, event.responses);
                          const selectedISO = nearestSlotISO(userResponse?.arrivalTime, slots);
                          const startMs = new Date(event.start_time).getTime();
                          return (
                            <div>
                              <p className={`text-xs font-medium mb-1.5 ${theme.textSecondary}`}>When will you arrive?</p>
                              <div className="flex flex-wrap gap-1.5">
                                {slots.map(slot => {
                                  const iso = slot.toISOString();
                                  const onTime = slot.getTime() <= startMs;
                                  const count = slotCounts[iso] || 0;
                                  const isSelected = selectedISO === iso;
                                  return (
                                    <button
                                      key={iso}
                                      type="button"
                                      onClick={() => handleArrivalTimeSelect(event, iso)}
                                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                                        isSelected
                                          ? onTime
                                            ? 'bg-green-100 border-green-500 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                            : 'bg-yellow-100 border-yellow-500 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                          : `${theme.backgroundSecondary} ${theme.border} ${theme.textSecondary} hover:opacity-80`
                                      }`}
                                    >
                                      <Clock className="w-3 h-3 shrink-0" />
                                      <span>{formatSlotTime(slot)}</span>
                                      <span className={`text-[10px] ${onTime ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'} ${isSelected ? 'opacity-80' : ''}`}>
                                        {onTime ? 'On time' : 'Late'}
                                      </span>
                                      <span className={`flex items-center gap-0.5 ${count > 0 ? 'text-blue-500 dark:text-blue-400' : theme.textMuted}`}>
                                        <Users className="w-3 h-3" />
                                        {count}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <Calendar className={`w-12 h-12 ${theme.textMuted} mx-auto mb-3`} />
                <p className={`${theme.textMuted}`}>No events scheduled for today</p>
              </div>
            )}
          </div>
        )}

        {calendarView === 'weekly' && (
          <div className="grid grid-cols-7 gap-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className={`text-center text-sm font-semibold ${theme.textMuted} pb-2`}>
                {day}
              </div>
            ))}
            {getWeekDays().map((date, index) => {
              const dayEvents = getEventsForDate(date);
              const isToday = date.toDateString() === new Date().toDateString();
              return (
                <div
                  key={index}
                  className={`min-h-24 p-2 rounded-lg border ${theme.border} ${
                    isToday ? 'bg-blue-50 dark:bg-blue-900/20' : theme.backgroundSecondary
                  }`}
                >
                  <div className={`text-sm font-medium ${isToday ? 'text-blue-600 font-bold' : theme.text} mb-1`}>
                    {date.getDate()}
                  </div>
                  <div className="space-y-1">
                    {/* Bookings first, and never the ones hidden behind "+N".
                        A day holding a court's opening window and somebody's
                        hire showed both as identical blue tiles, sorted by
                        time — so the window won the two visible slots and the
                        booking, the only thing on that day anyone is looking
                        for, collapsed into "+1". */}
                    {sortedForCell(dayEvents).slice(0, 2).map(event => {
                      const booked = isBookingEvent(event);
                      return (
                        <button
                          key={event.id}
                          onClick={() => handleEventClick(event)}
                          className={`w-full text-left text-xs rounded px-1 py-0.5 transition-colors ${
                            booked
                              ? 'bg-emerald-100 dark:bg-emerald-800 text-emerald-900 dark:text-emerald-100 hover:bg-emerald-200 dark:hover:bg-emerald-700'
                              : 'bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100 hover:bg-blue-200 dark:hover:bg-blue-700'
                          }`}
                          title={`${booked ? 'Booked — ' : ''}${event.subject} - ${event.start_time_display} to ${event.end_time_display}`}
                        >
                          <div className="truncate font-medium">{event.subject}</div>
                          <div className="text-xs opacity-75">{event.start_time_display}</div>
                        </button>
                      );
                    })}
                    {dayEvents.length > 2 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); openDay(date); }}
                        className="text-xs text-blue-500 hover:text-blue-400 hover:underline"
                      >
                        +{dayEvents.length - 2} more
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {calendarView === 'monthly' && (
          <div className="grid grid-cols-7 gap-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className={`text-center text-sm font-semibold ${theme.textMuted} pb-2`}>
                {day}
              </div>
            ))}
            {getMonthDays().map((date, index) => {
              if (!date) {
                return <div key={index} className="min-h-20" />;
              }
              const dayEvents = getEventsForDate(date);
              const isToday = date.toDateString() === new Date().toDateString();
              return (
                <div
                  key={index}
                  className={`min-h-20 p-2 rounded-lg border ${theme.border} ${
                    isToday ? 'bg-blue-50 dark:bg-blue-900/20' : theme.backgroundSecondary
                  }`}
                >
                  <div className={`text-sm font-medium ${isToday ? 'text-blue-600 font-bold' : theme.text} mb-1`}>
                    {date.getDate()}
                  </div>
                  {/* Three, not one.
                      A court's day routinely holds an opening window and the
                      bookings inside it, so one visible event per cell meant
                      something was always hidden — and sorted by time it was
                      the hour somebody had paid for, behind the hour that was
                      merely for sale. Reordering only chose which fact to lose.
                      Most days hold few events, so at three the "+N" becomes
                      the exception rather than the normal state of the grid. */}
                  <div className="space-y-1">
                    {sortedForCell(dayEvents).slice(0, 3).map(event => {
                      const booked = isBookingEvent(event);
                      return (
                        <button
                          key={event.id}
                          onClick={() => handleEventClick(event)}
                          className={`w-full text-left text-xs rounded px-1 py-0.5 transition-colors ${
                            booked
                              ? 'bg-emerald-100 dark:bg-emerald-800 text-emerald-900 dark:text-emerald-100 hover:bg-emerald-200 dark:hover:bg-emerald-700'
                              : 'bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100 hover:bg-blue-200 dark:hover:bg-blue-700'
                          }`}
                          title={`${booked ? 'Booked — ' : ''}${event.subject} - ${event.start_time_display} to ${event.end_time_display}`}
                        >
                          <div className="truncate font-medium">{event.subject}</div>
                          <div className="text-xs opacity-75">{event.start_time_display}</div>
                        </button>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); openDay(date); }}
                        className="text-xs text-blue-500 hover:text-blue-400 hover:underline"
                      >
                        +{dayEvents.length - 3} more
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {calendarView === 'list' && listMode === 'compact' && (
          <div className={`divide-y ${theme.border}`}>
            {getListUnits().length > 0 ? (
              getListUnits().slice(listPage * LIST_PAGE_SIZE, (listPage + 1) * LIST_PAGE_SIZE).map(group => {
                const first = group.events[0];
                const last  = group.events[group.events.length - 1];
                const isOpen = openSeries.has(group.key);
                const span = first.start_date_display === last.start_date_display
                  ? first.start_date_display
                  : `${first.start_date_display} – ${last.start_date_display}`;

                return (
                  <div key={group.key} className={`py-3 border-l-4 ${theme.borderAccent} pl-4`}>
                    <div className="flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => setOpenSeries(prev => {
                          const next = new Set(prev);
                          next.has(group.key) ? next.delete(group.key) : next.add(group.key);
                          return next;
                        })}
                        className="flex items-center gap-2 min-w-0 text-left flex-1"
                      >
                        <ChevronRight
                          className={`w-4 h-4 flex-shrink-0 ${theme.textMuted} transition-transform ${isOpen ? 'rotate-90' : ''}`}
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className={`font-semibold ${theme.text} truncate`}>{first.subject}</h4>
                            <KindChip event={first} />
                          </div>
                          <p className={`text-xs ${theme.textMuted}`}>
                            {group.seriesId
                              ? `${group.events.length} session${group.events.length === 1 ? '' : 's'} · ${span}`
                              : `${span} · ${first.start_time_display}`}
                          </p>
                        </div>
                      </button>
                      {/* Who a whole series asks: opened on its next date and
                          reaching every date after it. */}
                      {isAdmin && ownerType === 'group' && group.events.some(e => !isEventPast(e)) && (
                        <button
                          onClick={() => {
                            setSelectedEvent(group.events.find(e => !isEventPast(e)));
                            setInviteesScope(group.seriesId ? 'future' : 'this');
                            setShowInviteesModal(true);
                          }}
                          className={`p-1 rounded text-blue-500 hover:text-blue-600 ${theme.cardHover} flex-shrink-0`}
                          title={group.seriesId ? 'Edit invitees for every upcoming date' : 'Edit invitees'}
                          aria-label={group.seriesId ? 'Edit invitees for the series' : 'Edit invitees'}
                        >
                          <Users className="w-4 h-4" />
                        </button>
                      )}
                      {(isAdmin || first.createdBy === getCurrentUser()?.id) && (
                        <button
                          // Clicking the pencil on a series row already says
                          // which events are meant, so it opens straight on the
                          // whole series. The form's own selector is there to
                          // narrow it, which is a change of mind rather than a
                          // question that had to be answered up front.
                          onClick={() => openEditForm(first, group.seriesId ? 'all' : 'this')}
                          className={`p-1 rounded text-blue-500 hover:text-blue-600 ${theme.cardHover} flex-shrink-0`}
                          title={group.seriesId ? 'Edit all events in this series' : 'Edit event'}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* One form per group, under its header: editing the whole
                        series is started from a collapsed row, so there is no
                        occurrence row to host it. */}
                    {group.events.some(e => e.id === inlineEditId) && editEvent ? (
                      <div className="mt-3">{renderEditForm()}</div>
                    ) : isOpen && (
                      <div className="mt-2 ml-6 space-y-1">
                        {group.events.map(event => (
                          <div
                            key={event.id}
                            className={`flex items-center justify-between gap-3 py-1.5 text-sm ${isEventPast(event) ? 'opacity-50' : ''}`}
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-wrap">
                              <span className={theme.text}>{event.start_date_display}</span>
                              <span className={theme.textMuted}>
                                {event.start_time_display} - {event.end_time_display}
                              </span>
                              {event.capacity != null && (
                                <span className={`text-xs ${theme.textMuted}`}>· {event.capacity} spots</span>
                              )}
                              {summarisePrice(event.prices, isCourt ? '/hour' : null) && (
                                <span className={`text-xs font-medium ${theme.text}`}>
                                  · {summarisePrice(event.prices, isCourt ? '/hour' : null).text}
                                </span>
                              )}
                              {event.isRecurrenceException && (
                                <span className="text-xs text-amber-500">(modified)</span>
                              )}
                            </div>
                            {(isAdmin || event.createdBy === getCurrentUser()?.id) && (
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {isAdmin && ownerType === 'group' && !isEventPast(event) && (
                                  <button
                                    onClick={() => { setSelectedEvent(event); setInviteesScope('this'); setShowInviteesModal(true); }}
                                    className={`p-1 rounded text-blue-500 hover:text-blue-600 ${theme.cardHover}`}
                                    title="Edit invitees for this date"
                                    aria-label="Edit invitees for this date"
                                  >
                                    <Users className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                <button
                                  onClick={() => openEditForm(event, 'this')}
                                  className={`p-1 rounded text-blue-500 hover:text-blue-600 ${theme.cardHover}`}
                                  title="Edit this session"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteFromListClick(event)}
                                  className={`p-1 rounded text-red-400 hover:text-red-600 ${theme.cardHover}`}
                                  title="Delete event"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <Calendar className={`w-12 h-12 ${theme.textMuted} mx-auto mb-3`} />
                <p className={`${theme.textMuted}`}>No upcoming events</p>
              </div>
            )}
          </div>
        )}

        {calendarView === 'list' && listMode === 'expanded' && (
          <div className={`divide-y ${theme.border}`}>
            {getSortedEventsList().length > 0 ? (
              getSortedEventsList().slice(listPage * LIST_PAGE_SIZE, (listPage + 1) * LIST_PAGE_SIZE).map(event => {
                const userResponse = getUserResponseForEvent(event);
                const counts = getResponseCounts(event);

                // Being edited: the form takes the row's place, so the thing
                // being changed stays where it was on the page.
                if (inlineEditId === event.id && editEvent) {
                  return (
                    <div key={event.id} className={`py-4 border-l-4 ${theme.borderAccent} pl-4`}>
                      {renderEditForm()}
                    </div>
                  );
                }

                return (
                  <div key={event.id} className={`py-4 border-l-4 ${theme.borderAccent} pl-4`}>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className={`font-semibold ${theme.text}`}>{event.subject}</h4>
                          <KindChip event={event} />
                        </div>
                        {event.recurrenceRuleId && (
                          <span className={`inline-flex items-center gap-1 text-xs ${theme.link} mb-1`}>
                            <RefreshCw className="w-3 h-3" />
                            Recurring · {event.recurrenceRule?.frequency || 'series'}
                          </span>
                        )}
                        <div className="flex items-center gap-x-2 mt-1">
                          <Calendar className={`w-4 h-4 ${theme.textMuted}`} />
                          <span className={`text-sm ${theme.textMuted}`}>
                            {event.start_date_display}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {confirmDeleteId === event.id ? (
                          <>
                            <span className={`text-xs ${theme.textMuted}`}>Delete?</span>
                            <button
                              onClick={() => handleDeleteEventFromList(event.id)}
                              className={`${btn.base} ${btn.sm} ${theme.buttonDanger}`}
                            >
                              Yes
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className={`${btn.base} ${btn.sm} ${theme.buttonGhost}`}
                            >
                              No
                            </button>
                          </>
                        ) : (
                          // No View Details: the row below already shows
                          // everything that modal did. Edit opens in place.
                          (isAdmin || event.createdBy === getCurrentUser()?.id) && (
                            <>
                              {isAdmin && ownerType === 'group' && !isEventPast(event) && (
                                <button
                                  onClick={() => { setSelectedEvent(event); setShowInviteesModal(true); }}
                                  className={`p-1 rounded text-blue-500 hover:text-blue-600 ${theme.cardHover}`}
                                  title="Edit invitees"
                                  aria-label="Edit invitees"
                                >
                                  <Users className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => openEditForm(event, 'this')}
                                className={`p-1 rounded text-blue-500 hover:text-blue-600 ${theme.cardHover}`}
                                title="Edit event"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteFromListClick(event)}
                                className={`p-1 rounded text-red-400 hover:text-red-600 ${theme.cardHover}`}
                                title="Delete event"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )
                        )}
                      </div>
                    </div>
                    <div className="space-y-2 text-sm mb-3">
                      <div className="flex items-center gap-x-2">
                        <Clock className={`w-4 h-4 ${theme.textMuted}`} />
                        <span className={theme.textMuted}>
                          {event.start_time_display} - {event.end_time_display}
                        </span>
                      </div>
                      <div className="flex items-center gap-x-2">
                        <MapPin className={`w-4 h-4 ${theme.textMuted}`} />
                        <div className="min-w-0">
                          {event.venue?.name && (
                            <p className={`text-xs font-medium ${theme.textMuted}`}>{event.venue.name}</p>
                          )}
                          <p className={`text-xs ${theme.textMuted}`}>{event.venue?.fullAddress}</p>
                        </div>
                        {event.venue?.fullAddress && (
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venue.fullAddress)}${event.venue?.placeId ? `&query_place_id=${event.venue.placeId}` : ''}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center gap-x-1 text-xs ${theme.link} ${theme.linkHover} flex-shrink-0`}
                            title="Open in Google Maps"
                          >
                            <Navigation className="w-3 h-3" />
                            <span>Navigate</span>
                          </a>
                        )}
                      </div>
                      <SessionMeta event={event} courts={courts} theme={theme} compact unit={isCourt ? '/hour' : null} />
                      {event.permitUrl && (
                        <div className="flex items-center gap-x-2">
                          <FileText className={`w-4 h-4 ${theme.textMuted} flex-shrink-0`} />
                          <a
                            href={`${getWebOrigin()}/permits/${event.permitUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600"
                          >
                            View Permit PDF
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Response Counts */}
                    <div className="flex items-center gap-x-3 mb-3 text-xs">
                      <div className="flex items-center gap-x-1">
                        <Check className="w-3 h-3 text-green-600" />
                        <span className={theme.textMuted}>{counts.available}</span>
                      </div>
                      <div className="flex items-center gap-x-1">
                        <X className="w-3 h-3 text-red-600" />
                        <span className={theme.textMuted}>{counts.notAvailable}</span>
                      </div>
                      <div className="flex items-center gap-x-1">
                        <HelpCircle className="w-3 h-3 text-yellow-600" />
                        <span className={theme.textMuted}>{counts.maybe}</span>
                      </div>
                      <div className="flex items-center gap-x-1">
                        <Clock className="w-3 h-3 text-blue-600" />
                        <span className={theme.textMuted}>{counts.joinLate}</span>
                      </div>
                      {event.audience === 'invitees' && (
                        <span className={theme.textMuted}>No answer yet ({counts.invited})</span>
                      )}
                      {/* Who said what, by name, opened out under the counts. */}
                      <button
                        type="button"
                        onClick={() => toggleReplies(event.id)}
                        aria-expanded={openReplies.has(event.id)}
                        className={`ml-auto flex items-center gap-x-1 ${theme.link}`}
                      >
                        <span>{openReplies.has(event.id) ? 'Hide replies' : 'Who replied'}</span>
                        <ChevronRight className={`w-3 h-3 transition-transform ${openReplies.has(event.id) ? 'rotate-90' : ''}`} />
                      </button>
                    </div>
                    {openReplies.has(event.id) && (
                      <div className="mb-3">
                        <EventResponseGroups responses={event.responses} theme={theme} />
                      </div>
                    )}

                    {/* Availability Buttons */}
                    {allowResponses && isMember && !isEventPast(event) && (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <button
                            onClick={() => handleAvailabilityClick(event, 'available')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              userResponse?.status === 'available'
                                ? 'bg-green-500 text-white'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                          >
                            <Check className="w-4 h-4 inline mr-1" />
                            Available
                          </button>
                          <button
                            onClick={() => handleAvailabilityClick(event, 'not-available')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              userResponse?.status === 'not-available'
                                ? 'bg-red-500 text-white'
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                            }`}
                          >
                            <X className="w-4 h-4 inline mr-1" />
                            Not Available
                          </button>
                          <button
                            onClick={() => handleAvailabilityClick(event, 'maybe')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              userResponse?.status === 'maybe'
                                ? 'bg-yellow-500 text-white'
                                : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                            }`}
                          >
                            <HelpCircle className="w-4 h-4 inline mr-1" />
                            Maybe
                          </button>
                          <button
                            onClick={() => handleAvailabilityClick(event, 'join-late')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              userResponse?.status === 'join-late'
                                ? 'bg-blue-500 text-white'
                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            }`}
                          >
                            <Clock className="w-4 h-4 inline mr-1" />
                            Join Late
                          </button>
                        </div>
                        {event.start_time && (() => {
                          const slots = generateArrivalSlots(event);
                          const slotCounts = buildSlotCounts(slots, event.responses);
                          const selectedISO = nearestSlotISO(userResponse?.arrivalTime, slots);
                          const startMs = new Date(event.start_time).getTime();
                          return (
                            <div>
                              <p className={`text-xs font-medium mb-1.5 ${theme.textSecondary}`}>When will you arrive?</p>
                              <div className="flex flex-wrap gap-1.5">
                                {slots.map(slot => {
                                  const iso = slot.toISOString();
                                  const onTime = slot.getTime() <= startMs;
                                  const count = slotCounts[iso] || 0;
                                  const isSelected = selectedISO === iso;
                                  return (
                                    <button
                                      key={iso}
                                      type="button"
                                      onClick={() => handleArrivalTimeSelect(event, iso)}
                                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                                        isSelected
                                          ? onTime
                                            ? 'bg-green-100 border-green-500 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                            : 'bg-yellow-100 border-yellow-500 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                          : `${theme.backgroundSecondary} ${theme.border} ${theme.textSecondary} hover:opacity-80`
                                      }`}
                                    >
                                      <Clock className="w-3 h-3 shrink-0" />
                                      <span>{formatSlotTime(slot)}</span>
                                      <span className={`text-[10px] ${onTime ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'} ${isSelected ? 'opacity-80' : ''}`}>
                                        {onTime ? 'On time' : 'Late'}
                                      </span>
                                      <span className={`flex items-center gap-0.5 ${count > 0 ? 'text-blue-500 dark:text-blue-400' : theme.textMuted}`}>
                                        <Users className="w-3 h-3" />
                                        {count}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <Calendar className={`w-12 h-12 ${theme.textMuted} mx-auto mb-3`} />
                <p className={`${theme.textMuted}`}>No upcoming events</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Import from PDF Modal */}
      <ImportFromPdfModal
        isOpen={showImportPdfModal}
        onClose={() => setShowImportPdfModal(false)}
        onImport={handleImportFromPdf}
      />

      {/* Add Event Modal */}
      <AddEventModal
        isOpen={showAddEventModal}
        onClose={() => setShowAddEventModal(false)}
        onSave={handleAddEvent}
        // Capacity, courts and pricing only mean something for an offering's
        // sessions. A squad's kickabout has no academy behind it, so the fields
        // are not offered there rather than being offered and rejected.
        ownerType={ownerType}
        ownerId={ownerId}
        academyId={academyId}
        squadMembers={squadMembers}
        isClassSquad={isClassSquad}
      />

      {/* Who's invited — whoever runs a squad event changes it */}
      <EventInviteesModal
        isOpen={showInviteesModal}
        onClose={() => { setShowInviteesModal(false); setInviteesScope('this'); }}
        defaultScope={inviteesScope}
        isClassSquad={isClassSquad}
        event={selectedEvent}
        squadMembers={squadMembers}
        onSaved={() => { fetchEvents(); setShowEventDetailsModal(false); }}
      />

      {/* Event Details Modal */}
      {showEventDetailsModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.card} rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto`}>
            <div className="flex justify-between items-start mb-4">
              <h3 className={`text-xl font-semibold ${theme.text}`}>{selectedEvent.subject}</h3>
              <div className="flex items-center gap-x-2">
                {!showDeleteConfirm && isMember && (
                  <>
                    <button
                      onClick={() => openEditForm(selectedEvent, 'this')}
                      className={`p-2 rounded-lg ${theme.cardHover} text-blue-500 hover:text-blue-600`}
                      title="Edit event"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={handleDeleteEventClick}
                      className={`p-2 rounded-lg ${theme.cardHover} text-red-500 hover:text-red-600`}
                      title="Delete event"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </>
                )}
                <button
                  onClick={() => {
                    setShowEventDetailsModal(false);
                    setEditEvent(null);
                    setShowDeleteConfirm(false);
                  }}
                  className={`p-2 rounded-lg ${theme.cardHover}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {showDeleteConfirm ? (
                /* Delete Confirmation */
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800`}>
                    <div className="flex items-start gap-x-3">
                      <Trash2 className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-red-700 dark:text-red-400">Delete Event</h4>
                        <p className={`text-sm ${theme.textMuted} mt-1`}>
                          Are you sure you want to delete "{selectedEvent.subject}"? This action cannot be undone.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className={`${btn.base} ${btn.md} ${theme.buttonGhost} flex-1`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteEvent}
                      className={`${btn.base} ${btn.md} ${theme.buttonDanger} flex-1 flex items-center justify-center gap-2`}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Event
                    </button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <div className="space-y-2">
                  <div className="flex items-center gap-x-2">
                    <Calendar className={`w-5 h-5 ${theme.textMuted}`} />
                    <span className={theme.text}>
                      {selectedEvent.start_date_display}
                    </span>
                  </div>
                  {selectedEvent.recurrenceRuleId && (
                    <div className="flex items-center gap-x-2">
                      <RefreshCw className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                        {formatRecurrenceDescription(selectedEvent.recurrenceRule, selectedEvent.recurrenceIndex)}
                        {selectedEvent.isRecurrenceException && (
                          <span className="ml-2 text-xs text-amber-500 font-normal">(modified)</span>
                        )}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-x-2">
                    <Clock className={`w-5 h-5 ${theme.textMuted}`} />
                    <span className={theme.text}>
                      {selectedEvent.start_time_display} - {selectedEvent.end_time_display}
                    </span>
                  </div>
                  <div className="flex items-start gap-x-2">
                    <MapPin className={`w-5 h-5 ${theme.textMuted} flex-shrink-0 mt-0.5`} />
                    <div className="min-w-0 flex-1">
                      {selectedEvent.venue?.name && (
                        <p className={`text-sm font-medium ${theme.text}`}>{selectedEvent.venue.name}</p>
                      )}
                      <p className={`text-sm ${theme.text}`}>{selectedEvent.venue?.fullAddress}</p>
                      {(selectedEvent.venue?.city || selectedEvent.venue?.state) && (
                        <p className={`text-xs ${theme.textMuted}`}>
                          {[selectedEvent.venue.city, selectedEvent.venue.state, selectedEvent.venue.country].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                    {selectedEvent.venue?.fullAddress && (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedEvent.venue.fullAddress)}${selectedEvent.venue?.placeId ? `&query_place_id=${selectedEvent.venue.placeId}` : ''}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-x-1 text-xs ${theme.link} ${theme.linkHover} flex-shrink-0`}
                        title="Open in Google Maps"
                      >
                        <Navigation className="w-3 h-3" />
                        <span>Navigate</span>
                      </a>
                    )}
                  </div>
                  <SessionMeta event={selectedEvent} courts={courts} theme={theme} unit={isCourt ? '/hour' : null} />
                  {selectedEvent.permitUrl && (
                    <div className="flex items-center gap-x-2">
                      <FileText className={`w-5 h-5 ${theme.textMuted} flex-shrink-0`} />
                      <a
                        href={`${getWebOrigin()}/permits/${selectedEvent.permitUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600"
                      >
                        View Permit PDF
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Response Summary - only show in view mode */}
              {!showDeleteConfirm && selectedEvent.responses && selectedEvent.responses.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    {/* An invitation nobody has answered is not a response. */}
                    <h4 className={`font-semibold ${theme.text}`}>Responses ({selectedEvent.responses.filter(r => r.status !== 'invited').length})</h4>
                    {isAdmin && ownerType === 'group' && !isEventPast(selectedEvent) && (
                      <button
                        onClick={() => setShowInviteesModal(true)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium ml-auto mr-3"
                      >
                        Edit invitees
                      </button>
                    )}
                    <button
                      onClick={() => setShowResponseDetails(!showResponseDetails)}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      {showResponseDetails ? 'Hide Details' : 'View Details'}
                    </button>
                  </div>

                  {!showResponseDetails ? (
                    // Compact Summary View
                    <div className="flex flex-wrap gap-3">
                      {(() => {
                        const counts = {
                          available: selectedEvent.responses.filter(r => r.status === 'available').length,
                          notAvailable: selectedEvent.responses.filter(r => r.status === 'not-available').length,
                          maybe: selectedEvent.responses.filter(r => r.status === 'maybe').length,
                          joinLate: selectedEvent.responses.filter(r => r.status === 'join-late').length,
                          invited: selectedEvent.responses.filter(r => r.status === 'invited').length
                        };

                        return (
                          <>
                            {counts.available > 0 && (
                              <div className="flex items-center gap-x-2 px-3 py-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                                <Check className="w-4 h-4 text-green-600" />
                                <span className="text-sm font-medium text-green-700 dark:text-green-400">
                                  Available ({counts.available})
                                </span>
                              </div>
                            )}
                            {counts.notAvailable > 0 && (
                              <div className="flex items-center gap-x-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                                <X className="w-4 h-4 text-red-600" />
                                <span className="text-sm font-medium text-red-700 dark:text-red-400">
                                  Not Available ({counts.notAvailable})
                                </span>
                              </div>
                            )}
                            {counts.maybe > 0 && (
                              <div className="flex items-center gap-x-2 px-3 py-2 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                                <HelpCircle className="w-4 h-4 text-yellow-600" />
                                <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
                                  Maybe ({counts.maybe})
                                </span>
                              </div>
                            )}
                            {counts.joinLate > 0 && (
                              <div className="flex items-center gap-x-2 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                                <Clock className="w-4 h-4 text-blue-600" />
                                <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
                                  Join Late ({counts.joinLate})
                                </span>
                              </div>
                            )}
                            {counts.invited > 0 && (
                              <div className={`flex items-center gap-x-2 px-3 py-2 rounded-lg ${theme.backgroundSecondary} border ${theme.border}`}>
                                <HelpCircle className={`w-4 h-4 ${theme.textMuted}`} />
                                <span className={`text-sm font-medium ${theme.textMuted}`}>
                                  No answer yet ({counts.invited})
                                </span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    // Detailed View with Names - Scrollable Container
                    <EventResponseGroups responses={selectedEvent.responses} theme={theme} />
                  )}
                </div>
              )}

              {/* Availability Buttons - only show in view mode for upcoming events */}
              {!showDeleteConfirm && allowResponses && isMember && !isEventPast(selectedEvent) && (
                <div className="space-y-2 mt-6">
                  <div className="flex gap-2">
                    <button
                      onClick={() => { handleAvailabilityClick(selectedEvent, 'available'); setShowEventDetailsModal(false); }}
                      className="flex-1 px-4 py-2 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 text-sm font-medium"
                    >
                      <Check className="w-4 h-4 inline mr-1" />
                      Available
                    </button>
                    <button
                      onClick={() => { handleAvailabilityClick(selectedEvent, 'not-available'); setShowEventDetailsModal(false); }}
                      className="flex-1 px-4 py-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 text-sm font-medium"
                    >
                      <X className="w-4 h-4 inline mr-1" />
                      Not Available
                    </button>
                    <button
                      onClick={() => { handleAvailabilityClick(selectedEvent, 'maybe'); setShowEventDetailsModal(false); }}
                      className="flex-1 px-4 py-2 rounded-lg bg-yellow-100 text-yellow-700 hover:bg-yellow-200 text-sm font-medium"
                    >
                      <HelpCircle className="w-4 h-4 inline mr-1" />
                      Maybe
                    </button>
                    <button
                      onClick={() => { handleAvailabilityClick(selectedEvent, 'join-late'); setShowEventDetailsModal(false); }}
                      className="flex-1 px-4 py-2 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 text-sm font-medium"
                    >
                      <Clock className="w-4 h-4 inline mr-1" />
                      Join Late
                    </button>
                  </div>
                  {selectedEvent?.start_time && (() => {
                    const modalUserResponse = getUserResponseForEvent(selectedEvent);
                    const slots = generateArrivalSlots(selectedEvent);
                    const slotCounts = buildSlotCounts(slots, selectedEvent.responses);
                    const selectedISO = nearestSlotISO(modalUserResponse?.arrivalTime, slots);
                    const startMs = new Date(selectedEvent.start_time).getTime();
                    return (
                      <div>
                        <p className={`text-xs font-medium mb-1.5 ${theme.textSecondary}`}>When will you arrive?</p>
                        <div className="flex flex-wrap gap-1.5">
                          {slots.map(slot => {
                            const iso = slot.toISOString();
                            const onTime = slot.getTime() <= startMs;
                            const count = slotCounts[iso] || 0;
                            const isSelected = selectedISO === iso;
                            return (
                              <button
                                key={iso}
                                type="button"
                                onClick={() => handleArrivalTimeSelect(selectedEvent, iso)}
                                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                                  isSelected
                                    ? onTime
                                      ? 'bg-green-100 border-green-500 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                      : 'bg-yellow-100 border-yellow-500 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                    : `${theme.backgroundSecondary} ${theme.border} ${theme.textSecondary} hover:opacity-80`
                                }`}
                              >
                                <Clock className="w-3 h-3 shrink-0" />
                                <span>{formatSlotTime(slot)}</span>
                                <span className={`text-[10px] ${onTime ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'} ${isSelected ? 'opacity-80' : ''}`}>
                                  {onTime ? 'On time' : 'Late'}
                                </span>
                                <span className={`flex items-center gap-0.5 ${count > 0 ? 'text-blue-500 dark:text-blue-400' : theme.textMuted}`}>
                                  <Users className="w-3 h-3" />
                                  {count}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Which occurrences a delete applies to. Editing no longer asks here:
          the pencil that was clicked implies the scope, and the form carries a
          selector to change it. */}
      <RecurringScopeModal
        isOpen={scopeModal.open}
        action={scopeModal.action}
        onClose={() => setScopeModal({ open: false, action: null, eventId: null })}
        onSelect={(scope) => {
          const { action, eventId } = scopeModal;
          setScopeModal({ open: false, action: null, eventId: null });
          if (action === 'delete') {
            // For 'all'/'following' we need to refresh after delete
            deleteEventAPI(eventId, scope).then(result => {
              if (result.success) {
                toast.success('Event deleted');
                if (scope !== 'this') fetchEvents();
              } else {
                toast.error(result.error || 'Failed to delete');
              }
            });
            setShowDeleteConfirm(false);
            setShowEventDetailsModal(false);
            setSelectedEvent(null);
          }
        }}
      />

      {/* Apply to series confirmation */}
      <ApplyToSeriesConfirm
        isOpen={seriesConfirm.open}
        onCancel={() => setSeriesConfirm({ open: false, pendingArgs: null })}
        onConfirm={(applyToSeries) => {
          const { eventId, status, arrivalTime } = seriesConfirm.pendingArgs;
          setSeriesConfirm({ open: false, pendingArgs: null });
          handleUpdateAvailability(eventId, status, arrivalTime, applyToSeries);
        }}
      />

    </div>
  );
};

export default React.memo(CalendarAndEvent);
